import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useEffect } from 'react';
import { ModalWindow, Text, useLocalizeMessage } from 'libs.nucleus';
import { StepIndicator } from 'libs.react';
import { CloneChartSelect } from './clone_chart_select.component';
import { CreateChartForm } from './create_chart_form.component';
import { CreateChartOptionSelect } from './create_chart_option_select.component';
import { useCharts } from '../../hooks';
export const CreateChartModal = ({ onClose, onSuccess }) => {
    const translate = useLocalizeMessage();
    const { createChart, cloneChart, fetchChartById, fetchCharts, charts, isLoading, generateUniqueCloneName } = useCharts();
    // Step navigation
    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 2;
    const isFirstStep = currentStep === 1;
    const isLastStep = currentStep === totalSteps;
    // Step 1: Creation option
    const [creationOption, setCreationOption] = useState('create');
    // Clone flow state
    const [selectedChartId, setSelectedChartId] = useState(null);
    const [isLoadingCharts, setIsLoadingCharts] = useState(false);
    // Form state (used for create flow)
    const [formName, setFormName] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [selectedDatasetId, setSelectedDatasetId] = useState('');
    const [selectedDatasetKey, setSelectedDatasetKey] = useState('');
    const [isFormValid, setIsFormValid] = useState(false);
    // Fetch available charts when entering clone flow step 2
    useEffect(() => {
        if (creationOption === 'clone' && currentStep === 2) {
            const loadCharts = async () => {
                setIsLoadingCharts(true);
                try {
                    await fetchCharts();
                }
                catch (error) {
                    // Error handled in hook
                }
                finally {
                    setIsLoadingCharts(false);
                }
            };
            loadCharts();
        }
    }, [creationOption, currentStep, fetchCharts]);
    // Reset form when creation option changes
    useEffect(() => {
        setSelectedChartId(null);
        setFormName('');
        setFormDescription('');
        setSelectedDatasetId('');
        setSelectedDatasetKey('');
        setIsFormValid(false);
    }, [creationOption]);
    const handleFormChange = useCallback((name, description) => {
        setFormName(name);
        setFormDescription(description);
    }, []);
    const handleDatasetChange = useCallback((datasetId, datasetKey) => {
        setSelectedDatasetId(datasetId);
        setSelectedDatasetKey(datasetKey);
    }, []);
    const handleValidationChange = useCallback((isValid) => {
        setIsFormValid(isValid);
    }, []);
    const handleCloseModal = useCallback(() => {
        setCreationOption('create');
        setSelectedChartId(null);
        setFormName('');
        setFormDescription('');
        setSelectedDatasetId('');
        setSelectedDatasetKey('');
        setIsFormValid(false);
        setCurrentStep(1);
        onClose();
    }, [onClose]);
    const handleNext = useCallback(() => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    }, [currentStep]);
    const handlePrevious = useCallback(() => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    }, [currentStep]);
    const handleSubmit = useCallback(async () => {
        try {
            let newChart;
            if (creationOption === 'clone' && selectedChartId) {
                const sourceChart = await fetchChartById(selectedChartId);
                if (!sourceChart) {
                    throw new Error('Source chart not found');
                }
                const uniqueName = await generateUniqueCloneName(sourceChart.name);
                newChart = await cloneChart(sourceChart, 'CLONE', { name: uniqueName });
            }
            else {
                newChart = await createChart({
                    name: formName.trim(),
                    description: formDescription.trim() || undefined,
                    cubeConfigId: selectedDatasetId,
                    cubeConfigKey: selectedDatasetKey,
                });
            }
            onSuccess(newChart);
            handleCloseModal();
        }
        catch (error) {
            // Error handled in hook
        }
    }, [
        creationOption,
        selectedChartId,
        cloneChart,
        createChart,
        generateUniqueCloneName,
        fetchChartById,
        formName,
        formDescription,
        selectedDatasetId,
        selectedDatasetKey,
        onSuccess,
        handleCloseModal,
    ]);
    // Button configurations
    const isSubmitDisabled = useMemo(() => {
        if (creationOption === 'clone') {
            return isLoading || !selectedChartId;
        }
        return isLoading || !isFormValid;
    }, [creationOption, isLoading, selectedChartId, isFormValid]);
    const continueButton = {
        label: translate('Continue'),
        onClick: handleNext,
    };
    const createButton = {
        label: translate('Create'),
        onClick: handleSubmit,
        disabled: isSubmitDisabled,
    };
    const footerSecondaryActionButtons = isFirstStep
        ? [
            {
                label: translate('Cancel'),
                onClick: handleCloseModal,
            },
        ]
        : [
            {
                label: translate('Cancel'),
                onClick: handleCloseModal,
            },
            {
                label: translate('Back'),
                onClick: handlePrevious,
            },
        ];
    const stepFooter = (_jsxs("div", { className: 'flex justify-center items-center', children: [_jsx(Text, { size: 'sm', className: 'px-6', children: translate('Step {current} of {total}', {
                    current: currentStep,
                    total: totalSteps,
                }) }), _jsx(StepIndicator, { currentStep: currentStep, totalSteps: totalSteps })] }));
    return (_jsx(ModalWindow, { title: translate('Create chart'), isOpen: true, closeWindow: handleCloseModal, footerPrimaryActionButton: isLastStep ? createButton : continueButton, footerSecondaryActionButtons: footerSecondaryActionButtons, footerCenterContent: stepFooter, width: 'full', children: _jsxs("div", { className: 'min-h-[400px] size-full', children: [currentStep === 1 && (_jsx(CreateChartOptionSelect, { selectedOption: creationOption, onOptionChange: setCreationOption })), currentStep === 2 && creationOption === 'create' && (_jsx(CreateChartForm, { name: formName, description: formDescription, selectedDatasetId: selectedDatasetId, onFormChange: handleFormChange, onDatasetChange: handleDatasetChange, onValidationChange: handleValidationChange })), currentStep === 2 && creationOption === 'clone' && (_jsx(CloneChartSelect, { charts: charts, isLoading: isLoadingCharts, selectedChartId: selectedChartId, onChartSelect: setSelectedChartId }))] }) }));
};
